import { deburr, unescape as unescapeHtml } from 'lodash';

// Client-side twin of the experiences list matching (ExperienceRepository
// .addSearchFilters / TextNormalizer in koobv2-api-node): accent-insensitive,
// case-insensitive and order-tolerant, so a user who types a couple of words
// taken from the middle of a name still finds it. Lists that are already fully
// loaded in the browser (the Additional services modal) filter with this
// instead of a raw `includes`, so both search fields behave the same way.

// "and" — also "&" and "+", folded below — is a connector, not a required
// term: "villas & spa" must still find "Villas Spa" (KD-3471).
const DROPPABLE_WORDS = ['and'];

export function normalizeSearchText(value) {
  // NFD first: lodash deburr alone leaves Vietnamese tone marks in place
  // ("Hà Nội"), while deburr still handles what NFD does not decompose (Đ, ß,
  // œ, æ). unescape covers names stored HTML-encoded ("Spa &amp; Massage").
  return deburr(unescapeHtml(String(value ?? '')).normalize('NFD'))
    .replace(/[&+]/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function toSearchWords(term) {
  const normalized = normalizeSearchText(term);
  if (!normalized) return [];

  const words = normalized.split(' ').filter(Boolean);

  // Connectors are only dropped while something else remains to search on. A
  // lone "and" (or "&" / "+", folded above) is the whole search term, so it has
  // to filter like any other word instead of quietly showing the full list.
  const meaningful = words.filter((word) => !DROPPABLE_WORDS.includes(word));
  return meaningful.length ? meaningful : words;
}

// A search made of digits alone, "#" tolerated ("123", "#123"), is also a
// KOOB id: the list endpoints match ids containing those digits, anywhere
// (KD-3530), so the client-side twin does the same. Returns the digits, or
// null when the search is not an id.
const ID_SEARCH = /^#?(\d+)$/;

export function toSearchIdDigits(words) {
  if (words?.length !== 1) return null;
  const match = ID_SEARCH.exec(words[0]);
  return match ? match[1] : null;
}

// Every typed word must appear in at least one of the fields, independently of
// order and adjacency ("tour ayutthaya" finds "Ayutthaya day tour"). No words
// means no filtering, as an empty search shows the whole list. When the search
// is an id (see toSearchIdDigits) an item whose `id` contains those digits
// matches as well.
export function matchesSearchWords(words, fields, id) {
  if (!words?.length) return true;

  const idDigits = toSearchIdDigits(words);
  if (idDigits && id != null && String(id).includes(idDigits)) return true;

  const haystacks = (Array.isArray(fields) ? fields : [fields])
    .map(normalizeSearchText)
    .filter(Boolean);
  if (!haystacks.length) return false;

  return words.every((word) =>
    haystacks.some((haystack) => haystack.includes(word)),
  );
}
